import { useCallback, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	ANALYTICS_CHANNEL,
	ANALYTICS_SOURCE,
	ANALYTICS_TAG,
	NAV_CONTAINER,
	NAV_LAYER,
	NAV_VERSION,
	type UniversalCreateSourceProduct,
} from '@atlassian/universal-create-common';

export interface ObjectCreatedProps {
	crossAppCreation: boolean; // true if the object was created from a cross-app suggestion
	itemAti: string;
	sourceProduct?: string; // The product from which the object was created
	sourceSubProduct?: string; // The sub-product from which the object was created
}

export interface ObjectCreatedResultProps extends ObjectCreatedProps {
	actionSubjectId: string;
	result: 'success' | 'failed';
}

export interface FormButtonClickedAttributes {
	action?: string;
	actionSubjectId: string;
	crossAppCreation: ObjectCreatedProps['crossAppCreation'];
	isCreateSuccess?: boolean;
	itemAti: ObjectCreatedProps['itemAti'];
	itemProduct: UniversalCreateSourceProduct; // The native product of the item being created
	sourceProduct?: ObjectCreatedProps['sourceProduct'];
	sourceSubProduct?: ObjectCreatedProps['sourceSubProduct'];
}

export interface FlagClickedAttributes extends Omit<FormButtonClickedAttributes, 'action'> {
	flagName: string; // Identifies which flag emitted the interaction (e.g., 'issueCreateSuccess')
}

export interface TopLevelAttributes {
	actionSubject: string;
	eventType: string;
	source: string;
	tags: string[];
}

export interface PlatformCreateAnalyticsEvents {
	fireFlagControlAttributes: (attributes: FlagClickedAttributes) => void;
	fireFormControlAttributes: (attributes: FormButtonClickedAttributes) => void;
	fireObjectCreated: (attributes: ObjectCreatedProps) => void;
	fireObjectCreatedResult: (attributes: ObjectCreatedResultProps) => void;
}

export interface BaseObjectAttributes extends Pick<
	FormButtonClickedAttributes,
	'itemAti' | 'itemProduct' | 'sourceProduct' | 'sourceSubProduct'
> {}

export function usePlatformCreateAnalytics(): PlatformCreateAnalyticsEvents {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const baseAttributes = useMemo(
		() => ({
			navLayer: NAV_LAYER,
			navContainer: NAV_CONTAINER,
			navVersion: NAV_VERSION,
		}),
		[],
	);

	const topLevelAttributes = useMemo(
		() => ({
			actionSubject: 'universalCreate',
			eventType: 'operational',
			source: ANALYTICS_SOURCE,
			tags: [ANALYTICS_TAG],
		}),
		[],
	);

	const fireObjectCreated = useCallback(
		(attributes: ObjectCreatedProps) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'created',
				actionSubjectId: 'universalCreateObject',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	const fireObjectCreatedResult = useCallback(
		({ actionSubjectId, result, ...attributes }: ObjectCreatedResultProps) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: 'track',
				action: result,
				actionSubjectId,
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// createModal viewed Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/90539
	// createModalBackButton clicked Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/90540
	// createModalCancelButton clicked Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/90541
	// createModalCreateButton clicked Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/90542
	const fireFormControlAttributes = useCallback(
		({ actionSubjectId, action = 'clicked', ...attributes }: FormButtonClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: 'ui',
				actionSubject: 'universalCreate',
				action,
				actionSubjectId,
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	const fireFlagControlAttributes = useCallback(
		({ actionSubjectId, ...attributes }: FlagClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: 'ui',
				actionSubject: 'universalCreate',
				action: 'clicked',
				actionSubjectId,
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	return {
		fireFlagControlAttributes,
		fireObjectCreated,
		fireObjectCreatedResult,
		fireFormControlAttributes,
	};
}
